import { useEffect } from 'react'
import { Box, Button, Dialog, DialogActions, DialogContent, Stack, Typography } from '@mui/material'
import Markdown from 'react-markdown'
import { useLocation, useNavigate } from 'react-router-dom'
import { CHANGELOG_ROUTES } from '@/constant/routes'
import { useAuthContext } from '@/features/auth/context'
import useChangelog from '@/hooks/useChangelog'
import useTranslation from '@/hooks/useTranslation'

const formatDate = (dateString: string): string => {
  try {
    const [year, month, day] = dateString.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  } catch {
    return dateString
  }
}

// Pages where the popup may appear. Login, setup, no-interface and embeds are left out.
const POPUP_PATHS = ['/dashboard', '/config', '/admin']

type ChangelogPopupProps = {
  /** True while another prompt (e.g. "New version, reload") is open. */
  blocked?: boolean
}

const ChangelogPopup = ({ blocked = false }: ChangelogPopupProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { isLogin } = useAuthContext()
  const { latestVersion, popupVersions, pendingSync, markAsSeen, saveSeen } =
    useChangelog()

  // Quietly save migrated / new-account / previously failed values.
  useEffect(() => {
    if (isLogin && pendingSync) void saveSeen(pendingSync)
  }, [isLogin, pendingSync, saveSeen])

  const handleClose = () => {
    markAsSeen()
  }

  const handleViewAll = () => {
    markAsSeen()
    navigate(CHANGELOG_ROUTES.index.to)
  }

  const onAllowedPage =
    POPUP_PATHS.some((p) => pathname.startsWith(p)) &&
    pathname !== CHANGELOG_ROUTES.index.to

  if (!isLogin || blocked || !onAllowedPage) return null
  if (!latestVersion || popupVersions.length === 0) return null

  const showVersionHeadings = popupVersions.length > 1

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { backgroundColor: 'background.default' },
      }}
    >
      <Box sx={{ backgroundColor: 'background.default' }}>
        <Stack sx={{ padding: '41px 34px 4px 34px' }}>
          <Typography
            sx={{
              color: 'text.main',
              fontWeight: 400,
              fontSize: '20px',
              lineHeight: '24px',
            }}
          >
            {t('WhatsNewInVersion', { version: popupVersions[0].version })}
          </Typography>
          <Typography
            sx={{
              color: 'grey.600',
              fontSize: '12px',
              mt: 0.5,
            }}
          >
            {formatDate(popupVersions[0].date)}
          </Typography>
        </Stack>

        <DialogContent
          sx={{
            paddingLeft: '34px',
            paddingRight: '34px',
            paddingBottom: '18px',
            color: 'text.main',
            '& p': {
              color: 'text.main',
              fontSize: '12px',
              lineHeight: 1.6,
              margin: '4px 0',
            },
            '& h3': {
              color: 'text.main',
              fontSize: '14px',
              fontWeight: 700,
              mt: 2,
              mb: 1,
            },
            '& ul': {
              margin: 0,
              paddingLeft: '20px',
            },
            '& li': {
              color: 'text.main',
              fontSize: '12px',
              lineHeight: 1.6,
              mb: 0.5,
            },
            '& strong': {
              color: 'text.main',
            },
            '& blockquote': {
              margin: 0,
              borderLeft: '3px solid',
              borderColor: 'primary.main',
              paddingLeft: '12px',
              mb: 1,
            },
            '& blockquote p': {
              color: 'grey.600',
              fontSize: '13px',
              margin: 0,
            },
          }}
        >
          {popupVersions.map((release, index) => (
            <Box key={release.version} sx={{ mt: index === 0 ? 0 : 3 }}>
              {showVersionHeadings && index > 0 && (
                <Typography
                  sx={{ color: 'text.main', fontSize: '14px', fontWeight: 700, mb: 0.5 }}
                >
                  v{release.version} · {formatDate(release.date)}
                </Typography>
              )}
              <Markdown>{release.content}</Markdown>
            </Box>
          ))}
        </DialogContent>

        <DialogActions sx={{ paddingBottom: '32px', paddingRight: '30px' }}>
          <Button
            variant="secondary"
            sx={{ width: '140px' }}
            onClick={handleViewAll}
          >
            {t('ViewAllChanges')}
          </Button>
          <Button
            variant="primary"
            sx={{ width: '99px' }}
            onClick={handleClose}
          >
            {t('commonActionsClose')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

export default ChangelogPopup
